import { memo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { IMatch, ITournament } from '@zmedia/bm-soccer-ui/interface';
import { unixToDate } from '@zmedia/bm-soccer-ui/utils/date';
import { ArrayUtils, TimeUtils } from '@zmedia/bm-soccer-ui/utils';
import { currentMatchLiveSelector } from '@zmedia/bm-soccer-ui/selectors/match';
import {
    Element,
    Icon,
    Image,
    List,
    RouterLink,
    Section,
} from '@zmedia/bm-soccer-ui/components';
import { URL_SOCCER_PAGE } from '@zmedia/bm-soccer-ui/modules/shared';
import { HeaderMatchScore } from './scores';
import styles from './style.module.scss';

dayjs.extend(isToday)

interface IProps extends IMatch {
    tournament: ITournament;
    isLive?: boolean;
}

export function MatchDetailHeader(props: IProps) {
    const {
        tournament,
        away,
        home,
        stadium,
        referee,
        round,
        tvChannels,
        isLive,
        video,
    } = props;

    const currentMatch = useSelector(currentMatchLiveSelector);

    const homeGoals = (isLive ? currentMatch?.home?.goals : home?.goals) || [];
    const awayGoals = (isLive ? currentMatch?.away?.goals : away?.goals) || [];

    const _isToday = dayjs(props.startTime * 1000).isToday();
    const isSameYear =
        unixToDate(props.startTime, 'YYYY') ===
        unixToDate(TimeUtils.currentSecords(), 'YYYY');

    let date = unixToDate(props.startTime, 'dddd, DD/MM/YYYY HH:mm');
    if (isSameYear) {
        date = unixToDate(props.startTime, 'dddd, DD [thg] MM HH:mm');
    }

    if (_isToday) {
        date = unixToDate(props.startTime, '[Hôm nay], HH:mm');
    }

    const renderGoals = (items: string[]) => {
        if (!items.length) {
            return null;
        }
        return items.map((item: string, i: number) => {
            const index = ArrayUtils.findIndex(item);
            return (
                <List.Item key={i}>
                    <Element renderAs="span">{item.slice(0, index)}</Element>
                    <Element renderAs="span">
                        {item.slice(index, item.length).toLowerCase()}
                    </Element>
                </List.Item>
            );
        });
    };

    return (
        <Section className={styles['match-header-section']} name="match-info">
            {tournament?.name && (
                <RouterLink
                    to={`${URL_SOCCER_PAGE.SUBPATH}${tournament.path}`}
                    className={styles['tournament-info']}
                    title={tournament.name}
                >
                    <Image src={tournament.logo} alt={tournament.name} />
                    {tournament.name} {round?.name ? '-' + round.name : ''}
                </RouterLink>
            )}
            <HeaderMatchScore {...props} />
            {(homeGoals.length > 0 || awayGoals.length > 0) && (
                <>
                    <Element className={styles['goal-container']}>
                        <List className={styles['goal-info']}>
                            {renderGoals(homeGoals || [])}
                        </List>
                        <Icon icon="goal" />
                        <List className={styles['goal-info']}>
                            {renderGoals(awayGoals || [])}
                        </List>
                    </Element>
                </>
            )}
            <Element className={styles['header-info-box']}>
                <List>
                    {date && (
                        <List.Item>
                            <Icon icon="calendar-day" />
                            <Element renderAs="time">{date}</Element>
                        </List.Item>
                    )}

                    {stadium?.name && (
                        <List.Item>
                            <Icon icon="stadium" />
                            <Element renderAs="span">{stadium.name}</Element>
                        </List.Item>
                    )}

                    {referee?.referee && (
                        <List.Item>
                            <Icon icon="whistle" />
                            <Element renderAs="span">{referee.referee}</Element>
                        </List.Item>
                    )}

                    {tvChannels && (
                        <List.Item>
                            <Icon icon="tv" />
                            <Element renderAs="span">{tvChannels}</Element>
                        </List.Item>
                    )}
                    {video && video.source === 1 && (
                        <RouterLink to={video.url} newTab>
                            <List.Item>
                                <Icon icon="youtube"></Icon>
                                Video
                            </List.Item>
                        </RouterLink>
                    )}
                </List>
            </Element>
        </Section>
    );
}

export default memo(MatchDetailHeader);
